import type { VariantProps } from 'class-variance-authority';

import type { badgeVariants } from '@/components/Badge/Badge';

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

/**
 * The Status reference frame, transcribed: 28 display labels and the badge variant each one
 * takes. Design metadata, not data. The qualifier chips the same component renders are at
 * the end. G-025: the dashboard writes "Paid" where the table says "Fully paid"; both map
 * to the same variant. G-032: "Refunded" is used as a payment and a product status.
 */
export const STATUS_VARIANTS: Record<string, BadgeVariant> = {
    // Order status
    'In progress': 'secondary',
    Completed: 'success',
    Cancelled: 'destructive',
    // Payment status
    'Fully paid': 'success',
    Paid: 'success',
    Refunded: 'destructive',
    'Deposit paid': 'warning',
    'Partially paid': 'warning',
    'Deposit unpaid': 'destructive',
    'Not paid': 'destructive',
    'Link expired': 'destructive',
    // Product status
    Pending: 'secondary',
    'Awaiting payment': 'warning',
    Confirmed: 'secondary',
    Secured: 'secondary',
    Received: 'secondary',
    Shipped: 'secondary',
    Delivered: 'success',
    'Exchange requested': 'warning',
    'Exchange in progress': 'warning',
    'Exchange shipped': 'warning',
    'Exchange delivered': 'success',
    'Cancellation requested': 'warning',
    'Cancellation confirmed': 'destructive',
    'Cancellation refunded': 'destructive',
    'Return requested': 'warning',
    'Refund in progress': 'warning',
    'Refund completed': 'destructive',
    // In both the order and product vocabularies, the same variant in each
    Deleted: 'outline',
    // Qualifier chips
    Ready: 'success',
    'on invoice': 'secondary',
    'business only': 'secondary',
};

/** Where the display label differs from the status value. */
export const STATUS_LABELS: Record<string, string> = {
    Pending: 'Pending confirmation',
};

export function statusVariant(status: string): BadgeVariant {
    const variant = STATUS_VARIANTS[status];
    if (variant) return variant;
    if (import.meta.env.DEV) {
        console.warn(
            `Unknown status "${status}": not in the Status reference table, rendering as secondary.`,
        );
    }
    return 'secondary';
}

export function statusLabel(status: string): string {
    return STATUS_LABELS[status] ?? status;
}

export type StatusTone = 'primary' | 'success' | 'warning' | 'destructive' | 'muted';

/**
 * The same table read as a text colour, for the places the design tints a status as text
 * rather than a badge (a product status under a thumbnail). Derived from the variant so
 * there is one colour table, not two.
 */
export function statusTone(status: string): StatusTone {
    switch (statusVariant(status)) {
        case 'success':
            return 'success';
        case 'warning':
            return 'warning';
        case 'destructive':
            return 'destructive';
        case 'outline':
            return 'muted';
        default:
            return 'primary';
    }
}
